// Message templates: the list, the Meta sync and the preview.
// Route: /templates
//
// Templates are authored and approved in Meta. This console keeps the local
// model in step with a sync, shows what a template will look like, and can drop
// one it no longer wants. There is no authoring form here on purpose.

import { useEffect, useState, type ReactNode } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Pager,
  Search,
  Spinner,
  Table,
  useAsync,
  useToast,
  type BadgeTone,
  type Column,
} from "../ui";
import { PAGE_SIZE, errMessage, ErrorBanner, useDebounced, useAccounts } from "./shared";

interface TemplateButton {
  type: string;
  text: string;
  url?: string;
  phone_number?: string;
}

interface SampleValue {
  component: string;
  param_name: string;
  value: string;
}

interface Template {
  id: string;
  whatsapp_account: string;
  meta_template_id: string;
  name: string;
  display_name: string;
  language: string;
  category: string;
  status: string;
  header_type: string;
  header_content: string;
  body_content: string;
  footer_content: string;
  buttons: TemplateButton[];
  sample_values: SampleValue[];
  created_at: string;
  updated_at: string;
}

interface TemplateListPayload {
  templates?: Template[];
  total?: number;
}

// Approval status is a state, so it uses the status tones. Category is a label
// and stays neutral rather than borrowing a status colour.
const TEMPLATE_STATUS_TONE: Record<string, BadgeTone> = {
  APPROVED: "good",
  PENDING: "warning",
  REJECTED: "critical",
  DRAFT: "neutral",
};

const HEADER_GLYPH: Record<string, string> = {
  IMAGE: "▣",
  VIDEO: "▶",
  DOCUMENT: "▤",
};

/**
 * Body text with its variables resolved, as React nodes.
 *
 * The Vue screen built an HTML string and ran it through DOMPurify. Building
 * nodes instead removes the sanitiser from the trust path entirely — nothing
 * from the template or its samples is ever parsed as markup.
 */
function renderPreview(text: string, samples: SampleValue[]): ReactNode[] {
  const byName = new Map<string, string>();
  for (const s of samples) if (s?.param_name && s.value) byName.set(s.param_name, s.value);

  const out: ReactNode[] = [];
  const pattern = /\{\{([^}]+)\}\}/g;
  let last = 0;
  let match = pattern.exec(text);
  let key = 0;
  while (match) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const name = match[1].trim();
    const sample = byName.get(name);
    out.push(
      sample !== undefined ? (
        <span className="tag" key={`s${key++}`}>
          {sample}
        </span>
      ) : (
        <span className="badge warning" key={`p${key++}`}>{`{{${name}}}`}</span>
      )
    );
    last = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const TEMPLATE_ACCOUNT_KEY = "templates_selected_account";

export function TemplatesView() {
  const { can } = useSession();
  const toast = useToast();
  const canDelete = can("templates", "delete");

  const accounts = useAccounts();
  const [account, setAccount] = useState<string>(
    () => localStorage.getItem(TEMPLATE_ACCOUNT_KEY) || "all"
  );
  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [query, account]);

  // A stored account that no longer exists would filter the list to nothing.
  useEffect(() => {
    if (!accounts.length) return;
    if (account !== "all" && !accounts.some((a) => a.name === account)) {
      setAccount("all");
      localStorage.setItem(TEMPLATE_ACCOUNT_KEY, "all");
    }
  }, [accounts, account]);

  const { data, error, loading, reload } = useAsync<TemplateListPayload>(
    () =>
      http.get<TemplateListPayload>(
        `/templates${qs({
          account: account === "all" ? "" : account,
          search: query,
          page,
          limit: PAGE_SIZE,
        })}`
      ),
    [account, query, page]
  );
  const templates = data?.templates ?? [];
  const total = data?.total ?? templates.length;

  const [syncing, setSyncing] = useState(false);
  const [preview, setPreview] = useState<Template | null>(null);
  const [toDelete, setToDelete] = useState<Template | null>(null);

  const chooseAccount = (value: string) => {
    setAccount(value);
    localStorage.setItem(TEMPLATE_ACCOUNT_KEY, value);
  };

  const sync = async () => {
    if (account === "all") {
      toast.push("Choose a single account before syncing", "critical");
      return;
    }
    setSyncing(true);
    try {
      const res = await http.post<{ message?: string; count?: number }>("/templates/sync", {
        whatsapp_account: account,
      });
      toast.push(res.message || "Templates synced from Meta", "good");
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not sync from Meta"), "critical");
    } finally {
      setSyncing(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/templates/${toDelete.id}`);
      toast.push("Template deleted", "good");
      setToDelete(null);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not delete the template"), "critical");
      throw err;
    }
  };

  const columns: Column<Template>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (t) => (
        <div>
          <div className="strong">{t.display_name || t.name}</div>
          <div className="mono muted">{t.name}</div>
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      render: (t) => <Badge tone="neutral">{t.category || "—"}</Badge>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (t) => (
        <Badge tone={TEMPLATE_STATUS_TONE[t.status] ?? "neutral"}>{t.status || "—"}</Badge>
      ),
    },
    { key: "language", header: "Language", sortable: true },
    {
      key: "header_type",
      header: "Header",
      render: (t) => (
        <span className="row">
          <span aria-hidden>{HEADER_GLYPH[t.header_type] ?? "✉"}</span>
          <span className="muted">{t.header_type || "NONE"}</span>
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (t) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn icon sm ghost" title="Preview" onClick={() => setPreview(t)}>
            ◉
          </button>
          <button
            className="btn icon sm ghost"
            title="Delete"
            disabled={!canDelete}
            onClick={() => setToDelete(t)}
          >
            ✕
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Templates</h1>
          <div className="sub">Pre-approved messages you can send outside the 24-hour window.</div>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => void sync()} disabled={syncing || account === "all"}>
            {syncing ? <Spinner label="Syncing" /> : "⟳"} Sync from Meta
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the templates" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your templates</h3>
          <select
            value={account}
            aria-label="Filter by account"
            style={{ width: 180 }}
            onChange={(e) => chooseAccount(e.target.value)}
          >
            <option value="all">All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.name}>
                {a.name}
              </option>
            ))}
          </select>
          <Search value={search} onChange={setSearch} placeholder="Search templates…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={templates}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching templates" : "No templates yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "Templates are written and approved in Meta. Pick an account and sync to pull them in."
                }
                icon="▣"
                action={
                  <button className="btn sm" onClick={() => void sync()} disabled={account === "all"}>
                    ⟳ Sync from Meta
                  </button>
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="templates"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        title={`Preview: ${preview?.display_name || preview?.name || ""}`}
      >
        {preview && (
          <>
            <div className="bubble outbound" style={{ maxWidth: "100%" }}>
              {preview.header_type && preview.header_type !== "NONE" && (
                <div style={{ marginBottom: 6 }}>
                  {preview.header_type === "TEXT" ? (
                    <span className="strong">{preview.header_content}</span>
                  ) : (
                    <span className="doc">
                      <span aria-hidden>{HEADER_GLYPH[preview.header_type] ?? "▤"}</span>
                      {preview.header_type}
                    </span>
                  )}
                </div>
              )}
              <div className="prewrap">
                {renderPreview(preview.body_content, preview.sample_values ?? [])}
              </div>
              {preview.footer_content && (
                <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                  {preview.footer_content}
                </div>
              )}
              {(preview.buttons ?? []).length > 0 && (
                <div className="choice">
                  {(preview.buttons ?? []).map((b, i) => (
                    <span key={i}>{b.text || "Button"}</span>
                  ))}
                </div>
              )}
            </div>

            <dl className="kv" style={{ marginTop: 16 }}>
              <dt>Status</dt>
              <dd>
                <Badge tone={TEMPLATE_STATUS_TONE[preview.status] ?? "neutral"}>
                  {preview.status || "—"}
                </Badge>
              </dd>
              <dt>Category</dt>
              <dd>{preview.category || "—"}</dd>
              <dt>Language</dt>
              <dd>{preview.language}</dd>
              {preview.meta_template_id && (
                <>
                  <dt>Meta ID</dt>
                  <dd>
                    <code className="mono">{preview.meta_template_id}</code>
                  </dd>
                </>
              )}
            </dl>
          </>
        )}
        <DialogFooter>
          <button className="btn" onClick={() => setPreview(null)}>
            Close
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void remove()}
        title="Delete this template?"
        body={`"${toDelete?.display_name || toDelete?.name || ""}" will be removed here and, if it was published, deleted in Meta.`}
        danger
      />
    </div>
  );
}
